import axiosInstance from '../utils/axiosConfig';
import { fetchHMSEntities as fetchCorporateEntityOptions } from './hmsEntityService';
import keepServerAnswer from '../utils/keepServerAnswer';

/**
 * Service for handling invoicing-related API calls
 * 
 * Endpoints:
 * - Reference data (payment terms, VAT rates, GL accounts, cost centers, templates)
 * - Outgoing invoices (create, from delivery, from proforma)
 * - Pro-forma invoices (create, convert)
 * - Incoming invoices (upload, OCR)
 * - Purchase orders
 */

// ============================================================================
// WHAT A DELIVERY-SCOPED OPERATOR IS LOOKING AT
// ============================================================================

/* The four invoice list screens are shared by two very different readers, and
 * an operator must never be left guessing which list they are on.
 *
 * A finance user sees their ENTITY's book, and `EntityScopeNotice` says so. A
 * trade-ops operator sees something else entirely: the invoices raised against
 * the deliveries they can open, across every entity (see
 * `invoicing.delivery_scoped_access.apply_invoice_visibility`, which skips
 * entity scoping for this tier on purpose). Showing them "Showing IMT Group
 * Singapore only" would be a plain untruth about a list they are being asked
 * to trust, so they get their own sentence instead.
 *
 * The words live here, once, because four screens render them.
 *
 * They are FUNCTIONS of the document the screen actually shows. Hard-coding
 * "invoices" put the sentence "Showing your deliveries' invoices" at the top of
 * the Purchase Orders screen and the Pro-forma screen, which is simply the
 * wrong noun — an operator reading "invoices" on a screen full of purchase
 * orders has been told something untrue about the very list they are being
 * asked to trust, which is the fault this banner exists to prevent. */
export const deliveryScopedListNotice = (plural = 'invoices') =>
  `You are seeing the ${plural} raised against the deliveries you can open. `
  + `${plural.charAt(0).toUpperCase()}${plural.slice(1)} with no delivery `
  + 'behind them — salaries, office costs, group charges — belong to Finance '
  + 'and are not listed here.';

export const deliveryScopedEmptyNotice = (plural = 'invoices') =>
  `Nothing has been raised yet against a delivery you can open. ${plural.charAt(0).toUpperCase()}${plural.slice(1)} `
  + 'with no delivery behind them are not shown here at all.';

/* The heading that sits in bold in front of the sentence above. Same reason:
 * the four screens show four different documents. */
export const deliveryScopedListHeading = (plural = 'invoices') =>
  `Showing your deliveries' ${plural}.`;

/* Matches EntityScopeNotice's plain (non-empty) banner so the two explanations
 * of "why this list is narrower than you expect" read as one thing. */
export const DELIVERY_SCOPED_NOTICE_STYLE = {
  margin: '0 0 1rem',
  padding: '0.85rem 1rem',
  borderRadius: '6px',
  fontSize: '0.9rem',
  border: '1px solid #cbd5e1',
  borderLeft: '4px solid #475569',
  background: '#f8fafc',
  color: '#334155',
};

// ============================================================================
// LIST-COMPLETENESS GUARD
// ============================================================================

/**
 * Refuse a partial invoice list instead of drawing it as if it were the whole book.
 *
 * The six invoice list screens ask for their rows with no page size and render
 * whatever comes back. That is correct TODAY only because these endpoints are
 * unpaged — they answer with the whole set. The day anyone adds pagination to
 * an invoice viewset, every one of those screens would quietly start showing
 * page one and saying nothing about the rest.
 *
 * That is the `silent-list-truncation` shape this codebase has already been
 * bitten by five times in one day (Bunker Enquiries showing 25 of 69 live
 * enquiries, the admin audit trail filtering 50 of 576 rows, and so on). It
 * costs nothing to close now and is very expensive to notice later, because a
 * short list looks exactly like a short book.
 *
 * So: if the response ever arrives as a DRF page envelope that does not hold
 * everything, this throws. The list screens all catch a failed load and show
 * the reason in a banner, so the trader is told "we could not show you all of
 * it" rather than being handed a slice. Refusing loudly is the point — the
 * alternative is a wrong number on an invoice screen.
 *
 * A bare array (today's shape) and a complete envelope both pass through
 * untouched, so nothing changes until the day paging appears.
 *
 * @param {*} data - the raw response body
 * @param {string} label - plural noun for the banner ("outgoing invoices")
 * @returns {*} the same body, when it is complete
 */
/**
 * What the guard throws.
 *
 * A PLAIN `Error` was the first version of this and it reintroduced the exact
 * bug the guard was written to prevent. Every consumer of these fetchers
 * classifies a rejection by looking for `error.response.status` — a plain Error
 * has no `.response`, so it fell through the "was this a refusal?" branch and
 * landed in the `= []` default. The screen then rendered an empty invoice list
 * with no explanation: the recorded HMS shape "a 403 swallowed into no
 * results", produced by the guard against silent truncation. A guard whose
 * failure is silent is not a guard.
 *
 * So the throw is TYPED and self-describing:
 *   - `isListTruncation` — a boolean flag, not `instanceof`. Babel's ES5
 *     transpile of `class X extends Error` breaks `instanceof` in some builds,
 *     and a check that quietly stops matching is how this class of bug returns.
 *   - `userMessage` — the sentence to put in front of the user, ready to render.
 * Use `describeListFailure` below rather than reading these by hand.
 */
export class InvoiceListTruncatedError extends Error {
  constructor(message, { label, shown, total }) {
    super(message);
    this.name = 'InvoiceListTruncatedError';
    // See above: the load-bearing check, deliberately not `instanceof`.
    this.isListTruncation = true;
    this.label = label;
    this.shown = shown;
    this.total = total;
    this.userMessage = message;
    // Restores the prototype chain when this file is transpiled down to ES5,
    // so `instanceof` still works for anyone who reaches for it anyway.
    Object.setPrototypeOf(this, InvoiceListTruncatedError.prototype);
  }
}

export const assertWholeInvoiceList = (data, label) => {
  if (!data || Array.isArray(data) || typeof data !== 'object') return data;
  if (!Array.isArray(data.results)) return data;

  const shown = data.results.length;
  // `count` absent means we cannot tell — fall back to what we were sent
  // rather than invent a total and then compare against our own invention.
  const total = typeof data.count === 'number' ? data.count : shown;

  if (data.next || shown < total) {
    throw new InvoiceListTruncatedError(
      `Only ${shown} of ${total} ${label} could be loaded — this screen has no ` +
      `way to reach the rest, so it is not showing you the whole list. Ask an ` +
      `administrator to fix the invoice list before trusting this screen.`,
      { label, shown, total },
    );
  }
  return data;
};

/**
 * Turn a failed invoice-list fetch into something a screen can SAY.
 *
 * There are three different answers a screen can get and they must never be
 * drawn the same way:
 *
 *   - `refused`   — the server said no (403/401). "No invoices" is a lie.
 *   - `truncated` — the guard above fired. The rows we hold are a slice of the
 *                   book, so showing them without a word is a wrong number.
 *   - `failed`    — anything else (network, 500, a bug). Still not emptiness.
 *
 * And the fourth answer, which is NOT a failure and never reaches here: the
 * list genuinely came back empty. Every consumer of the wrapped fetchers routes
 * its rejections through this so that all three failures are shown and only a
 * real empty list renders as empty.
 *
 * @param {*} error - the rejection
 * @param {string} what - what was being loaded, for the sentence ("the invoices
 *                        for this delivery")
 * @returns {{kind: 'refused'|'truncated'|'failed', message: string, status: number|undefined}}
 */
export const describeListFailure = (error, what = 'this list') => {
  if (error && error.isListTruncation) {
    return { kind: 'truncated', message: error.userMessage || error.message, status: undefined };
  }

  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return {
      kind: 'refused',
      message:
        `${what} could not be loaded because your account was refused access to `
        + 'them. Ask an administrator for Financials access, or for this '
        + 'delivery to be added to your operations grouping.',
      status,
    };
  }

  return {
    kind: 'failed',
    message:
      `${what} could not be loaded${status ? ` (the server answered ${status})` : ''}. `
      + 'This is not the same as there being none — try again, and tell IT if it keeps happening.',
    status,
  };
};

/* Which failure wins when several lists fail at once. A refusal is the most
 * actionable thing we can tell someone, and a truncation is a wrong-number
 * warning, so both outrank a generic breakage. */
const LIST_FAILURE_SEVERITY = { refused: 3, truncated: 2, failed: 1 };

/**
 * The single worst failure out of several parallel list loads, or null when
 * they all succeeded. Used by any screen that fans out to more than one list.
 */
export const worstListFailure = (failures) => {
  const real = (failures || []).filter(Boolean);
  if (real.length === 0) return null;
  return real.reduce(
    (worst, candidate) =>
      (LIST_FAILURE_SEVERITY[candidate.kind] || 0) > (LIST_FAILURE_SEVERITY[worst.kind] || 0)
        ? candidate
        : worst,
  );
};

// ============================================================================
// SEARCHING A LIST SCREEN
// ============================================================================

/* The six list endpoints, keyed by the `invoiceType` the search bar is handed,
 * with the plural noun the guard puts in its message.
 *
 * This map exists because `InvoiceSearchFilter` used to hold its own copy of
 * these URLs and call axios directly — so the search bar on a screen protected
 * by the completeness guard was the one control that walked straight past it,
 * and its catch turned every failure into `onSearchResults([])`. Searching for
 * an invoice you are not allowed to see, or a page-two invoice, both answered
 * "no results found". One list of endpoints, one guarded door. */
const INVOICE_LIST_ENDPOINTS = {
  outgoing: ['/api/invoicing/outgoing-invoices/', 'outgoing invoices'],
  proforma: ['/api/invoicing/proforma-invoices/', 'pro-forma invoices'],
  incoming: ['/api/invoicing/incoming-invoices/', 'incoming invoices'],
  'purchase-order': ['/api/invoicing/purchase-orders/', 'purchase orders'],
  credit_note: ['/api/invoicing/credit-notes/', 'credit notes'],
  debit_note: ['/api/invoicing/debit-notes/', 'debit notes'],
};

/**
 * Run the list search behind a screen's search bar.
 *
 * Refuses an unknown `invoiceType` loudly instead of falling through to some
 * default endpoint: a silent string-match fallthrough here would search the
 * wrong book and look like it worked.
 *
 * @param {string} invoiceType - one of the keys of INVOICE_LIST_ENDPOINTS
 * @param {URLSearchParams|object} params - the query
 * @returns {Promise<Array>} the rows, always an array
 */
export const searchInvoiceList = async (invoiceType, params) => {
  const entry = INVOICE_LIST_ENDPOINTS[invoiceType];
  if (!entry) {
    throw new Error(
      `Cannot search "${invoiceType}": no invoice list is registered under that `
      + 'name. This is a wiring fault, not something you did.'
    );
  }
  const [endpoint, label] = entry;
  const query = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params).toString();
  const response = await axiosInstance.get(query ? `${endpoint}?${query}` : endpoint);
  const data = assertWholeInvoiceList(response.data, label);
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

// ============================================================================
// REFERENCE DATA API CALLS
// ============================================================================

/**
 * Fetch all payment terms
 */
export const fetchPaymentTerms = async () => {
  try {
    const response = await axiosInstance.get('/api/invoicing/payment-terms/');
    return response.data;
  } catch (error) {
    console.error('Error fetching payment terms:', error);
    throw error;
  }
};

/**
 * Fetch template type options
 */
export const fetchTemplateTypeOptions = async () => {
  try {
    const response = await axiosInstance.get('/api/invoicing/template-type-options/');
    return response.data;
  } catch (error) {
    console.error('Error fetching template type options:', error);
    throw error;
  }
};

/**
 * Fetch all VAT rates
 */
export const fetchVATRates = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/vat-rates/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching VAT rates:', error);
    throw error;
  }
};

/**
 * Fetch VAT rates allowed for a specific HMS entity.
 * Falls back to all active rates if the entity has none configured.
 */
export const fetchVATRatesByEntity = async (entityId, { showInactive = false, page, search, filters } = {}) => {
  try {
    const params = { entity_id: entityId };
    if (showInactive) params.show_inactive = 'true';
    if (page) params.page = page;
    if (search) params.search = search;
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
    const response = await axiosInstance.get('/api/invoicing/vat-rates/', { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching VAT rates for entity ${entityId}:`, error);
    throw error;
  }
};

/**
 * Fetch VAT rates for specific country
 */
export const fetchVATRatesByCountry = async (countryCode) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/vat-rates/by_country/?country=${countryCode}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching VAT rates for ${countryCode}:`, error);
    throw error;
  }
};

export const createVATRate = async (data) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/vat-rates/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating VAT rate:', error);
    throw error;
  }
};

export const updateVATRate = async (id, data) => {
  try {
    const response = await axiosInstance.patch(`/api/invoicing/vat-rates/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating VAT rate:', error);
    throw error;
  }
};

export const deleteVATRate = async (id) => {
  const response = await axiosInstance.delete(`/api/invoicing/vat-rates/${id}/`);
  return response.data;
};

export const uploadVATRatesCSV = async (file, entityId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (entityId) formData.append('entity_id', entityId);
    const response = await axiosInstance.post('/api/invoicing/vat-rates/upload-csv/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading VAT rates CSV:', error);
    throw error;
  }
};

/**
 * Fetch all GL accounts (optionally filtered by entity)
 */
export const fetchGLAccounts = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/gl-accounts/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching GL accounts:', error);
    throw error;
  }
};

/** IMT's one shared chart of accounts (the GL accounts page lists and uploads to it). */
export const SHARED_CHART = 'shared';

export const fetchGLAccountsByEntity = async (entityId, { page, search, filters } = {}) => {
  try {
    // SHARED_CHART = IMT's one chart of accounts for every company.
    const params = entityId === SHARED_CHART
      ? { shared: 'true', show_inactive: 'true' }
      : { entity: entityId, show_inactive: 'true' };
    if (page) params.page = page;
    if (search) params.search = search;
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
    const response = await axiosInstance.get('/api/invoicing/gl-accounts/', { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching GL accounts for entity ${entityId}:`, error);
    throw error;
  }
};

export const createGLAccount = async (data) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/gl-accounts/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating GL account:', error);
    throw error;
  }
};

export const updateGLAccount = async (id, data) => {
  try {
    const response = await axiosInstance.patch(`/api/invoicing/gl-accounts/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating GL account:', error);
    throw error;
  }
};

export const deleteGLAccount = async (id) => {
  const response = await axiosInstance.delete(`/api/invoicing/gl-accounts/${id}/`);
  return response.data;
};

export const uploadGLAccountsCSV = async (file, entityId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (entityId === SHARED_CHART) {
      formData.append('shared', 'true');   // IMT's one chart of accounts
    } else {
      formData.append('entity_id', entityId);
    }
    const response = await axiosInstance.post('/api/invoicing/gl-accounts/upload-csv/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading GL accounts CSV:', error);
    throw error;
  }
};

/**
 * Search the standardized IFRS reference chart for the GL account picker.
 * Returns a capped list of { id, account_number, account_title, balance, depth }.
 */
export const fetchIFRSAccounts = async (search, { limit = 25 } = {}) => {
  try {
    const params = { limit };
    if (search) params.search = search;
    const response = await axiosInstance.get('/api/invoicing/ifrs-accounts/', { params });
    return response.data?.results || response.data || [];
  } catch (error) {
    console.error('Error fetching IFRS accounts:', error);
    // Row 128: this returned [], which the picker read as "no match — will be
    // saved as a custom code". A failed search is not an empty one.
    throw keepServerAnswer(error, "Couldn't load the chart of accounts.");
  }
};

/**
 * Fetch all cost centers (optionally filtered by entity)
 */
export const fetchCostCenters = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/cost-centers/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching cost centers:', error);
    throw error;
  }
};

export const fetchCostCentersByEntity = async (entityId, { page, search, filters } = {}) => {
  try {
    const params = { entity: entityId, show_inactive: 'true' };
    if (page) params.page = page;
    if (search) params.search = search;
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
    }
    const response = await axiosInstance.get('/api/invoicing/cost-centers/', { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching cost centers for entity ${entityId}:`, error);
    throw error;
  }
};

export const createCostCenter = async (data) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/cost-centers/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating cost center:', error);
    throw error;
  }
};

export const updateCostCenter = async (id, data) => {
  try {
    const response = await axiosInstance.patch(`/api/invoicing/cost-centers/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating cost center:', error);
    throw error;
  }
};

export const deleteCostCenter = async (id) => {
  const response = await axiosInstance.delete(`/api/invoicing/cost-centers/${id}/`);
  return response.data;
};

export const uploadCostCentresCSV = async (file, entityId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_id', entityId);
    const response = await axiosInstance.post('/api/invoicing/cost-centers/upload-csv/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading cost centres CSV:', error);
    throw error;
  }
};


/**
 * IMT-ONLY (divergence): pending CorporateEntity-derived cost centres.
 * HMS has no equivalent -- do NOT drop this when porting this file from HMS.
 */
export const fetchPendingIMTEntities = async () => {
  try {
    const response = await axiosInstance.get('/api/invoicing/cost-centers/pending-imt-entities/');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending IMT entities:', error);
    return { pending_entities: [], pending_centers: 0, unmatched_employees: [] };
  }
};

/**
 * IMT-ONLY (divergence 07): the cost centre made for each new delivery waits
 * for a Cost Centre Approver. HMS has no equivalent -- do NOT drop these when
 * porting this file from HMS. They throw on failure: a waiting list that could
 * not be loaded must say so, never read as "nothing waiting".
 */
export const fetchWaitingCostCentres = async () => {
  const response = await axiosInstance.get('/api/invoicing/cost-centers/waiting/');
  return response.data;
};

export const approveCostCentre = async (id, abacusCode) => {
  const body = abacusCode !== undefined ? { abacus_code: abacusCode } : {};
  const response = await axiosInstance.post(`/api/invoicing/cost-centers/${id}/approve/`, body);
  return response.data;
};

export const rejectCostCentre = async (id, reason = '') => {
  const response = await axiosInstance.post(`/api/invoicing/cost-centers/${id}/reject/`, { reason });
  return response.data;
};

export const approveCostCentres = async (ids, abacusCodes = {}) => {
  const response = await axiosInstance.post('/api/invoicing/cost-centers/approve-many/', { ids, abacus_codes: abacusCodes });
  return response.data;
};

/**
 * Cost Categories (global, not entity-scoped)
 */
export const fetchCostCategories = async (params = {}, { page, search, filters } = {}) => {
  try {
    const mergedParams = { ...params };
    if (page) mergedParams.page = page;
    if (search) mergedParams.search = search;
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) mergedParams[key] = value;
      });
    }
    const response = await axiosInstance.get('/api/invoicing/cost-categories/', { params: mergedParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching cost categories:', error);
    throw error;
  }
};

export const createCostCategory = async (data) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/cost-categories/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating cost category:', error);
    throw error;
  }
};

export const updateCostCategory = async (id, data) => {
  try {
    const response = await axiosInstance.patch(`/api/invoicing/cost-categories/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating cost category:', error);
    throw error;
  }
};

export const deleteCostCategory = async (id) => {
  const response = await axiosInstance.delete(`/api/invoicing/cost-categories/${id}/`);
  return response.data;
};

export const uploadCostCategoriesCSV = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/invoicing/cost-categories/upload-csv/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading cost categories CSV:', error);
    throw error;
  }
};

/**
 * Fetch entity options for dropdowns.
 *
 * Delegates to the corporate-structure gated list so only bridge-linked
 * entities appear; the raw /api/offer/hms-entities/ endpoint still returns
 * legacy HMS Bergbau rows. Keeps the {id, hms_entity_name} shape callers
 * expect.
 */
export const fetchHMSEntities = async () => {
  const options = await fetchCorporateEntityOptions();
  return options.map((o) => ({ id: o.value, hms_entity_name: o.label }));
};

/**
 * Fetch invoice templates for specific entity
 */
export const fetchTemplatesForEntity = async (entityId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/templates/for_entity/?entity_id=${entityId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

/**
 * Fetch banking details based on invoice type and entity/partner
 * 
 * @param {string} invoiceType - Type of invoice (OUTGOING, PROFORMA, or INCOMING)
 * @param {number|null} entityId - HMS entity ID (required for OUTGOING/PROFORMA)
 * @param {number|null} partnerId - Partner/CRM profile ID (required for INCOMING)
 * @param {string|null} pledgeBankKey - the document is pledged to this bank:
 *   only its accounts come back (customer documents only)
 * @returns {Promise<object>} Banking details data
 */
export const fetchBankingDetails = async (invoiceType, entityId = null, partnerId = null, pledgeBankKey = null) => {
  try {
    const params = { invoice_type: invoiceType };
    
    if (invoiceType === 'OUTGOING' || invoiceType === 'PROFORMA') {
      if (!entityId) {
        throw new Error('Entity ID is required for OUTGOING/PROFORMA invoices');
      }
      params.entity_id = entityId;
      if (pledgeBankKey) params.pledge_bank_key = pledgeBankKey;
    } else if (invoiceType === 'INCOMING') {
      if (!partnerId) {
        throw new Error('Partner ID is required for INCOMING invoices');
      }
      params.partner_id = partnerId;
    }
    
    const response = await axiosInstance.get('/api/invoicing/banking-details/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching banking details:', error);
    throw error;
  }
};

/**
 * The banks one of our companies holds accounts at: the Pledge bank dropdown.
 *
 * @param {number|string} entity - the company's id or name, as the screen stores it
 * @returns {Promise<{banks: Array<{key, name, account_count}>, company, problem}>}
 */
export const fetchPledgeBanks = async (entity) => {
  const response = await axiosInstance.get('/api/invoicing/pledge-banks/', { params: { entity } });
  return response.data;
};

/**
 * Fetch dropdown options needed for invoice Screen 1 (header).
 * GL accounts, VAT rates, and cost centres are handled by
 * SearchableSelect on the screens that actually use them.
 */
export const fetchInvoicingDropdowns = async () => {
  try {
    const [paymentTerms, templateTypes] = await Promise.all([
      fetchPaymentTerms(),
      fetchTemplateTypeOptions()
    ]);
    
    return {
      payment_terms: paymentTerms,
      template_types: templateTypes
    };
  } catch (error) {
    console.error('Error fetching invoicing dropdowns:', error);
    throw error;
  }
};

// ============================================================================
// OUTGOING INVOICE API CALLS
// ============================================================================

/**
 * Fetch all outgoing invoices
 */
export const fetchOutgoingInvoices = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/outgoing-invoices/', { params });
    return assertWholeInvoiceList(response.data, 'outgoing invoices');
  } catch (error) {
    console.error('Error fetching outgoing invoices:', error);
    throw error;
  }
};

/**
 * Fetch single outgoing invoice
 */
export const fetchOutgoingInvoice = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/outgoing-invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching outgoing invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Create new outgoing invoice
 */
export const createOutgoingInvoice = async (invoiceData) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/outgoing-invoices/', invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error creating outgoing invoice:', error);
    throw error;
  }
};

/**
 * Create outgoing invoice from delivery
 */
export const createOutgoingInvoiceFromDelivery = async (deliveryId, additionalData = {}) => {
  try {
    const payload = {
      delivery_id: deliveryId,
      ...additionalData
    };
    const response = await axiosInstance.post('/api/invoicing/outgoing-invoices/create_from_delivery/', payload);
    return response.data;
  } catch (error) {
    console.error(`Error creating invoice from delivery ${deliveryId}:`, error);
    throw error;
  }
};

/**
 * Update outgoing invoice
 */
/** Record a payment against an outgoing invoice: date, amount, bank reference.
 *  The invoice's status follows from the amount — full settlement marks it PAID,
 *  a short payment is recorded and leaves it unpaid. */
export const recordOutgoingInvoicePayment = async (id, payload) => {
  const response = await axiosInstance.post(
    `/api/invoicing/outgoing-invoices/${id}/record-payment/`, payload);
  return response.data;
};

/** Undo a recorded payment — clears date, amount and reference, and unsets PAID. */
export const clearOutgoingInvoicePayment = async (id) => {
  const response = await axiosInstance.post(
    `/api/invoicing/outgoing-invoices/${id}/clear-payment/`);
  return response.data;
};

export const recordIncomingInvoicePayment = async (id, payload) => {
  const response = await axiosInstance.post(
    `/api/invoicing/incoming-invoices/${id}/record-payment/`, payload);
  return response.data;
};

export const updateOutgoingInvoice = async (id, invoiceData) => {
  try {
    const response = await axiosInstance.put(`/api/invoicing/outgoing-invoices/${id}/`, invoiceData);
    return response.data;
  } catch (error) {
    console.error(`Error updating outgoing invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Approve outgoing invoice
 */
export const approveOutgoingInvoice = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/outgoing-invoices/${id}/approve/`);
    return response.data;
  } catch (error) {
    console.error(`Error approving invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Delete outgoing invoice (DRAFT only)
 */
export const deleteOutgoingInvoice = async (id) => {
  try {
    const response = await axiosInstance.delete(`/api/invoicing/outgoing-invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Void an outgoing invoice: creates a full reversing credit note (submitted for
 * approval) and marks the invoice VOIDED. Irreversible.
 */
export const voidOutgoingInvoice = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/outgoing-invoices/${id}/void/`);
    return response.data;
  } catch (error) {
    console.error(`Error voiding outgoing invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch the outgoing invoices that need chasing, for one reminder bucket.
 * bucket: 'overdue' (due_date < today) or 'due_soon' (today .. today+5).
 * Each row carries suggested_recipient_email + recipient_source so the
 * lightbox can prefill the address and disable rows with no CRM contact.
 */
export const fetchPaymentReminders = async (bucket = 'overdue') => {
  try {
    const response = await axiosInstance.get(
      '/api/invoicing/outgoing-invoices/payment-reminders/',
      { params: { bucket } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment reminders (${bucket}):`, error);
    throw error;
  }
};

/**
 * Send payment reminders for the ticked rows.
 * reminders: [{ id, recipient_email }] — the recipient may be a comma-separated
 * list. Which of the two texts goes out is decided by the backend from the
 * invoice's due date; the client never sends a template.
 * Returns { sent, failed, results: [{ id, status, error }] }.
 */
export const sendPaymentReminders = async (reminders) => {
  try {
    const response = await axiosInstance.post(
      '/api/invoicing/outgoing-invoices/send-payment-reminders/',
      reminders
    );
    return response.data;
  } catch (error) {
    console.error('Error sending payment reminders:', error);
    throw error;
  }
};

/**
 * Void an incoming invoice: creates a full reversing debit note (submitted for
 * approval) and marks the invoice VOIDED. Irreversible.
 */
export const voidIncomingInvoice = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/incoming-invoices/${id}/void/`);
    return response.data;
  } catch (error) {
    console.error(`Error voiding incoming invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Download outgoing invoice as PDF.
 * Pass { preview: true } to fetch the PDF for inline preview - this skips the
 * APPROVED-status gate and the PDF_GENERATED audit-log entry on the backend,
 * and never spends the invoice's one Original.
 *
 * Returns the blob, with `documentType` ('original' | 'copy') attached from the
 * response header so the caller can relabel its button without refetching the
 * list. Attached to the blob rather than returned in a wrapper so the three
 * existing callers keep working unchanged.
 */
export const downloadOutgoingInvoicePDF = async (invoiceId, { preview = false } = {}) => {
  try {
    const response = await axiosInstance.get(
      `/api/invoicing/outgoing-invoices/${invoiceId}/download-pdf/`,
      {
        responseType: 'blob',
        params: preview ? { preview: 1 } : undefined,
      }
    );
    const blob = response.data;
    try {
      blob.documentType = response.headers?.['x-invoice-document-type'] || null;
    } catch (e) {
      // Blob is frozen in some browsers; the label just falls back to a refetch.
    }
    return blob;
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    throw error;
  }
};

/**
 * Download pro-forma invoice PDF.
 * Pass { preview: true } to fetch the PDF for inline preview - this skips the
 * APPROVED-status gate and the PDF_GENERATED audit-log entry on the backend.
 *
 * @param {number} invoiceId - Pro-forma invoice ID
 * @param {{ preview?: boolean }} [options]
 * @returns {Promise<Blob>} PDF file blob
 */
export const downloadProformaInvoicePDF = async (invoiceId, { preview = false } = {}) => {
  try {
    const response = await axiosInstance.get(
      `/api/invoicing/proforma-invoices/${invoiceId}/download-pdf/`,
      {
        responseType: 'blob',
        params: preview ? { preview: 1 } : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error downloading pro-forma PDF:', error);
    throw error;
  }
};

// ============================================================================
// PRO-FORMA INVOICE API CALLS
// ============================================================================

/**
 * Fetch all pro-forma invoices
 */
export const fetchProformaInvoices = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/proforma-invoices/', { params });
    return assertWholeInvoiceList(response.data, 'pro-forma invoices');
  } catch (error) {
    console.error('Error fetching proforma invoices:', error);
    throw error;
  }
};

/**
 * Fetch single pro-forma invoice
 */
export const fetchProformaInvoice = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/proforma-invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching proforma invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Create new pro-forma invoice
 */
export const createProformaInvoice = async (invoiceData) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/proforma-invoices/', invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error creating proforma invoice:', error);
    throw error;
  }
};

/**
 * Submit pro-forma for conversion approval
 */
export const submitProformaForConversionApproval = async (proformaId) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/proforma-invoices/${proformaId}/submit_for_conversion_approval/`);
    return response.data;
  } catch (error) {
    console.error(`Error submitting proforma ${proformaId} for conversion:`, error);
    throw error;
  }
};

/**
 * Update pro-forma invoice
 */
export const updateProformaInvoice = async (id, invoiceData) => {
  try {
    const response = await axiosInstance.put(`/api/invoicing/proforma-invoices/${id}/`, invoiceData);
    return response.data;
  } catch (error) {
    console.error(`Error updating proforma invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Delete pro-forma invoice
 */
export const deleteProformaInvoice = async (id) => {
  try {
    await axiosInstance.delete(`/api/invoicing/proforma-invoices/${id}/`);
  } catch (error) {
    console.error(`Error deleting proforma invoice ${id}:`, error);
    throw error;
  }
};

// ============================================================================
// INCOMING INVOICE API CALLS
// ============================================================================

/**
 * Fetch all incoming invoices
 */
export const fetchIncomingInvoices = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/incoming-invoices/', { params });
    return assertWholeInvoiceList(response.data, 'incoming invoices');
  } catch (error) {
    console.error('Error fetching incoming invoices:', error);
    throw error;
  }
};

/**
 * Cross-check a received invoice against CRM (identity / IBAN / bank owner /
 * KYC). Advisory flags only — never blocks. Pass either
 * {incoming_invoice_id} for a saved invoice, or
 * {partner_id, supplier_name, iban, bank_account_id} while still creating.
 */
export const checkInvoiceCrmMatch = async (payload) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/crm-match-check/', payload);
    return response.data;
  } catch (error) {
    console.error('Error running CRM match check:', error);
    throw error;
  }
};

/**
 * Fetch single incoming invoice
 */
export const fetchIncomingInvoice = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/incoming-invoices/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching incoming invoice ${id}:`, error);
    throw error;
  }
};

/**
 * The documents filed in DocuWare for one supplier invoice (DocuWare's Items).
 */
export const fetchInvoiceDocuments = async (invoiceId) => {
  const response = await axiosInstance.get('/api/docuware/invoice-documents/documents/', {
    params: { invoice_id: invoiceId, count: 100 },
  });
  return response.data.Items || [];
};

/**
 * The PDF a supplier invoice was captured from, kept with it (a Blob).
 * Read through the invoice's own access rules on the server.
 */
export const fetchIncomingCaptureFile = async (id) => {
  const response = await axiosInstance.get(
    `/api/invoicing/incoming-invoices/${id}/capture-file/`, { responseType: 'blob' });
  return response.data;
};

/**
 * File the kept PDF into DocuWare again after a failure.
 * @returns {Promise<{filed: boolean, filing_problem: string|null}>}
 */
export const retryIncomingCaptureFiling = async (id) => {
  const response = await axiosInstance.post(`/api/invoicing/incoming-invoices/${id}/retry-filing/`);
  return response.data;
};

/**
 * Create new incoming invoice
 */
export const createIncomingInvoice = async (invoiceData) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/incoming-invoices/', invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error creating incoming invoice:', error);
    throw error;
  }
};

/**
 * Update incoming invoice
 */
/**
 * Attach the supplier's document to an invoice being created, WITHOUT reading it.
 *
 * Same upload door as Capture, told not to start the AI reading (`read=false`).
 * Returns `{ analysis_id, status: 'attached', filename }`; the form keeps the id
 * as `_capture_analysis_id` and the save files the document with the invoice.
 * For the desk that types invoices in from the Bills to Receive reminders and
 * does not want anything read over its typing (Marcel, 2026-09-30).
 */
export const attachSupplierDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('read', 'false');
  const response = await axiosInstance.post(
    '/api/invoicing/incoming-invoices/upload_and_capture/', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const updateIncomingInvoice = async (id, invoiceData) => {
  try {
    const response = await axiosInstance.put(`/api/invoicing/incoming-invoices/${id}/`, invoiceData);
    return response.data;
  } catch (error) {
    console.error(`Error updating incoming invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Delete incoming invoice
 */
export const deleteIncomingInvoice = async (id) => {
  try {
    await axiosInstance.delete(`/api/invoicing/incoming-invoices/${id}/`);
  } catch (error) {
    console.error(`Error deleting incoming invoice ${id}:`, error);
    throw error;
  }
};

/**
 * Upload supplier invoice for OCR extraction
 */
export const uploadSupplierInvoice = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post('/api/invoicing/incoming-invoices/upload_and_extract/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading supplier invoice:', error);
    throw error;
  }
};

// ============================================================================
// PURCHASE ORDER API CALLS
// ============================================================================

/**
 * Fetch all purchase orders
 */
export const fetchPurchaseOrders = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/purchase-orders/', { params });
    return assertWholeInvoiceList(response.data, 'purchase orders');
  } catch (error) {
    console.error('Error fetching purchase orders:', error);
    throw error;
  }
};

/**
 * Fetch single purchase order
 */
export const fetchPurchaseOrder = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/purchase-orders/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching purchase order ${id}:`, error);
    throw error;
  }
};

/**
 * Create new purchase order
 */
export const createPurchaseOrder = async (poData) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/purchase-orders/', poData);
    return response.data;
  } catch (error) {
    console.error('Error creating purchase order:', error);
    throw error;
  }
};

/**
 * Update purchase order
 */
export const updatePurchaseOrder = async (id, poData) => {
  try {
    const response = await axiosInstance.put(`/api/invoicing/purchase-orders/${id}/`, poData);
    return response.data;
  } catch (error) {
    console.error(`Error updating purchase order ${id}:`, error);
    throw error;
  }
};

/**
 * Delete purchase order
 */
export const deletePurchaseOrder = async (id) => {
  try {
    await axiosInstance.delete(`/api/invoicing/purchase-orders/${id}/`);
  } catch (error) {
    console.error(`Error deleting purchase order ${id}:`, error);
    throw error;
  }
};

/**
 * Mark purchase order as received
 */
export const markPurchaseOrderReceived = async (poId) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/purchase-orders/${poId}/mark_received/`);
    return response.data;
  } catch (error) {
    console.error(`Error marking PO ${poId} as received:`, error);
    throw error;
  }
};

// ============================================================================
// DASHBOARD API CALLS
// ============================================================================

/**
 * Fetch dashboard statistics for all invoice types
 */
export const fetchInvoicingDashboardStats = async () => {
  try {
    const response = await axiosInstance.get('/api/invoicing/dashboard/stats/');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

/**
 * Fetch recent invoices across all types
 */
export const fetchRecentInvoices = async (limit = 10) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/dashboard/recent/', {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching recent invoices:', error);
    throw error;
  }
};

// ============================================================================
// CREDIT NOTE (AR) API CALLS
// ============================================================================

/**
 * Fetch all credit notes
 */
export const fetchCreditNotes = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/credit-notes/', { params });
    return assertWholeInvoiceList(response.data, 'credit notes');
  } catch (error) {
    console.error('Error fetching credit notes:', error);
    throw error;
  }
};

/**
 * Fetch single credit note
 */
export const fetchCreditNote = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/credit-notes/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching credit note ${id}:`, error);
    throw error;
  }
};

/**
 * Create new credit note
 */
export const createCreditNote = async (creditNoteData) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/credit-notes/', creditNoteData);
    console.log('Credit note created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating credit note:', error);
    throw error;
  }
};

/**
 * Create credit note from outgoing invoice
 */
export const createCreditNoteFromInvoice = async (invoiceId) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/credit-notes/create_from_invoice/', {
      invoice_id: invoiceId
    });
    console.log(`Pre-filled credit note from invoice ${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error(`Error creating credit note from invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Update existing credit note
 */
export const updateCreditNote = async (id, creditNoteData) => {
  try {
    const response = await axiosInstance.put(`/api/invoicing/credit-notes/${id}/`, creditNoteData);
    console.log(`Credit note ${id} updated`);
    return response.data;
  } catch (error) {
    console.error(`Error updating credit note ${id}:`, error);
    throw error;
  }
};

/**
 * Delete credit note (DRAFT only)
 */
export const deleteCreditNote = async (id) => {
  try {
    const response = await axiosInstance.delete(`/api/invoicing/credit-notes/${id}/`);
    console.log(`Credit note ${id} deleted`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting credit note ${id}:`, error);
    throw error;
  }
};

/**
 * Mark credit note as applied
 */
export const markCreditNoteApplied = async (id, appliedDate = null, refundReference = '') => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/credit-notes/${id}/mark_applied/`, {
      applied_date: appliedDate,
      refund_reference: refundReference
    });
    console.log(`Credit note ${id} marked as applied`);
    return response.data;
  } catch (error) {
    console.error(`Error marking credit note ${id} as applied:`, error);
    throw error;
  }
};

/**
 * Download a credit note as PDF.
 * Pass { preview: true } to fetch it for inline preview - this skips the
 * APPROVED-status gate and the PDF_GENERATED audit-log entry on the backend.
 *
 * @param {number} id - Credit note ID
 * @param {{ preview?: boolean }} [options]
 * @returns {Promise<Blob>} PDF file blob
 */
export const downloadCreditNotePDF = async (id, { preview = false } = {}) => {
  try {
    const response = await axiosInstance.get(
      `/api/invoicing/credit-notes/${id}/download-pdf/`,
      {
        responseType: 'blob',
        params: preview ? { preview: 1 } : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error downloading credit note PDF:', error);
    throw error;
  }
};

/**
 * Generate PDF for credit note
 */
export const generateCreditNotePDF = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/credit-notes/${id}/generate_pdf/`);
    console.log(`PDF generated for credit note ${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error generating PDF for credit note ${id}:`, error);
    throw error;
  }
};

/**
 * Submit credit note for approval
 */
export const submitCreditNoteForApproval = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/credit-notes/${id}/submit_for_approval/`);
    console.log(`Credit note ${id} submitted for approval`);
    return response.data;
  } catch (error) {
    console.error(`Error submitting credit note ${id} for approval:`, error);
    throw error;
  }
};

/**
 * Approve credit note
 */
export const approveCreditNote = async (id, notes = '') => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/credit-notes/${id}/approve/`, { notes });
    console.log(`Credit note ${id} approved`);
    return response.data;
  } catch (error) {
    console.error(`Error approving credit note ${id}:`, error);
    throw error;
  }
};

/**
 * Reject credit note
 */
export const rejectCreditNote = async (id, reason) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/credit-notes/${id}/reject/`, { reason });
    console.log(`Credit note ${id} rejected`);
    return response.data;
  } catch (error) {
    console.error(`Error rejecting credit note ${id}:`, error);
    throw error;
  }
};

// ============================================================================
// DEBIT NOTE (AR) API CALLS
// ============================================================================

/**
 * Fetch all debit notes
 */
export const fetchDebitNotes = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/invoicing/debit-notes/', { params });
    return assertWholeInvoiceList(response.data, 'debit notes');
  } catch (error) {
    console.error('Error fetching debit notes:', error);
    throw error;
  }
};

/**
 * Fetch single debit note
 */
export const fetchDebitNote = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/debit-notes/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching debit note ${id}:`, error);
    throw error;
  }
};

/**
 * Create new debit note
 */
export const createDebitNote = async (debitNoteData) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/debit-notes/', debitNoteData);
    console.log('Debit note created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating debit note:', error);
    throw error;
  }
};

/**
 * Create debit note from outgoing invoice
 */
export const createDebitNoteFromInvoice = async (invoiceId) => {
  try {
    const response = await axiosInstance.post('/api/invoicing/debit-notes/create_from_invoice/', {
      invoice_id: invoiceId
    });
    console.log(`Pre-filled debit note from invoice ${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error(`Error creating debit note from invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Update existing debit note
 */
export const updateDebitNote = async (id, debitNoteData) => {
  try {
    const response = await axiosInstance.put(`/api/invoicing/debit-notes/${id}/`, debitNoteData);
    console.log(`Debit note ${id} updated`);
    return response.data;
  } catch (error) {
    console.error(`Error updating debit note ${id}:`, error);
    throw error;
  }
};

/**
 * Delete debit note (DRAFT only)
 */
export const deleteDebitNote = async (id) => {
  try {
    const response = await axiosInstance.delete(`/api/invoicing/debit-notes/${id}/`);
    console.log(`Debit note ${id} deleted`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting debit note ${id}:`, error);
    throw error;
  }
};

/**
 * Mark debit note as applied
 */
export const markDebitNoteApplied = async (id, appliedDate = null, paymentReference = '') => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/debit-notes/${id}/mark_applied/`, {
      applied_date: appliedDate,
      payment_reference: paymentReference
    });
    console.log(`Debit note ${id} marked as applied`);
    return response.data;
  } catch (error) {
    console.error(`Error marking debit note ${id} as applied:`, error);
    throw error;
  }
};

/**
 * Download a debit note as PDF.
 * Pass { preview: true } to fetch it for inline preview - this skips the
 * APPROVED-status gate and the PDF_GENERATED audit-log entry on the backend.
 *
 * @param {number} id - Debit note ID
 * @param {{ preview?: boolean }} [options]
 * @returns {Promise<Blob>} PDF file blob
 */
export const downloadDebitNotePDF = async (id, { preview = false } = {}) => {
  try {
    const response = await axiosInstance.get(
      `/api/invoicing/debit-notes/${id}/download-pdf/`,
      {
        responseType: 'blob',
        params: preview ? { preview: 1 } : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error downloading debit note PDF:', error);
    throw error;
  }
};

/**
 * Generate PDF for debit note
 */
export const generateDebitNotePDF = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/debit-notes/${id}/generate_pdf/`);
    console.log(`PDF generated for debit note ${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error generating PDF for debit note ${id}:`, error);
    throw error;
  }
};

/**
 * Submit debit note for approval
 */
export const submitDebitNoteForApproval = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/debit-notes/${id}/submit_for_approval/`);
    console.log(`Debit note ${id} submitted for approval`);
    return response.data;
  } catch (error) {
    console.error(`Error submitting debit note ${id} for approval:`, error);
    throw error;
  }
};

/**
 * Approve debit note
 */
export const approveDebitNote = async (id, notes = '') => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/debit-notes/${id}/approve/`, { notes });
    console.log(`Debit note ${id} approved`);
    return response.data;
  } catch (error) {
    console.error(`Error approving debit note ${id}:`, error);
    throw error;
  }
};

/**
 * Reject debit note
 */
export const rejectDebitNote = async (id, reason) => {
  try {
    const response = await axiosInstance.post(`/api/invoicing/debit-notes/${id}/reject/`, { reason });
    console.log(`Debit note ${id} rejected`);
    return response.data;
  } catch (error) {
    console.error(`Error rejecting debit note ${id}:`, error);
    throw error;
  }
};


// ============================================================================
// AUDIT LOG API CALLS
// ============================================================================

/**
 * Fetch audit logs for invoices
 * 
 * @param {string} invoiceType - Type of invoice ('outgoing', 'proforma', 'incoming', 'purchase-order')
 * @param {number|null} invoiceId - Specific invoice ID (null for all invoices of type)
 * @param {object} filters - Additional filters (action, etc.)
 * @returns {Promise<object>} Audit logs data
 */
export const fetchInvoiceAuditLogs = async (invoiceType, invoiceId = null, filters = {}) => {
  try {
    // Build the endpoint URL based on invoice type and whether we're fetching for a specific invoice
    let url;
    
    // Map invoice type to API endpoint
    const typeEndpointMap = {
      'outgoing': 'outgoing-invoices',
      'proforma': 'proforma-invoices',
      'incoming': 'incoming-invoices',
      'purchase-order': 'purchase-orders'
    };
    
    const endpoint = typeEndpointMap[invoiceType];
    if (!endpoint) {
      throw new Error(`Invalid invoice type: ${invoiceType}`);
    }
    
    if (invoiceId) {
      // Fetch audit logs for specific invoice
      url = `/api/invoicing/${endpoint}/${invoiceId}/audit-logs/`;
    } else {
      // Fetch all audit logs for invoice type
      url = `/api/invoicing/${endpoint}/audit_logs/`;
    }
    
    // Add filters as query parameters
    const params = {};
    if (filters.action) {
      params.action = filters.action;
    }
    
    const response = await axiosInstance.get(url, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching audit logs for ${invoiceType}:`, error);
    throw error;
  }
};

// ============================================================================
// FINANCIAL APPROVAL API CALLS
// ============================================================================

/**
 * Approve an outgoing invoice (Financial Approvers only)
 * 
 * @param {number} invoiceId - Invoice ID to approve
 * @param {string} notes - Optional approval notes
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const approveInvoice = async (invoiceId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/outgoing-invoices/${invoiceId}/approve/`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving invoice:', error);
    throw error;
  }
};

/**
 * Reject an outgoing invoice (Financial Approvers only)
 * 
 * @param {number} invoiceId - Invoice ID to reject
 * @param {string} reason - Required rejection reason
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const rejectInvoice = async (invoiceId, reason) => {
  try {
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is required');
    }
    
    const response = await axiosInstance.post(
      `/api/invoicing/outgoing-invoices/${invoiceId}/reject/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting invoice:', error);
    throw error;
  }
};

/**
 * Undo an approval / "un-stamp" an outgoing invoice (Financial Approvers only).
 * Reverts an APPROVED invoice back to PENDING_APPROVAL. Only allowed before the
 * invoice has been sent.
 *
 * @param {number} invoiceId - Invoice ID to un-approve
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const unapproveInvoice = async (invoiceId) => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/outgoing-invoices/${invoiceId}/unapprove/`
    );
    return response.data;
  } catch (error) {
    console.error('Error undoing invoice approval:', error);
    throw error;
  }
};

/**
 * Get invoices pending approval (Financial Approvers only)
 *
 * @returns {Promise<object>} List of invoices with status PENDING_APPROVAL
 */
export const getPendingApprovals = async () => {
  try {
    const response = await axiosInstance.get(
      '/api/invoicing/outgoing-invoices/pending_approvals/'
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    throw error;
  }
};

/**
 * Approve a pro-forma invoice (Financial Approvers only)
 * 
 * @param {number} invoiceId - Pro-forma invoice ID to approve
 * @param {string} notes - Optional approval notes
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const approveProformaInvoice = async (invoiceId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/proforma-invoices/${invoiceId}/approve/`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving pro-forma invoice:', error);
    throw error;
  }
};

/**
 * Reject a pro-forma invoice (Financial Approvers only)
 * 
 * @param {number} invoiceId - Pro-forma invoice ID to reject
 * @param {string} reason - Required rejection reason
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const rejectProformaInvoice = async (invoiceId, reason) => {
  try {
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is required');
    }
    
    const response = await axiosInstance.post(
      `/api/invoicing/proforma-invoices/${invoiceId}/reject/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting pro-forma invoice:', error);
    throw error;
  }
};

/**
 * Approve pro-forma conversion (Financial Approvers only)
 * 
 * @param {number} invoiceId - Pro-forma invoice ID to approve conversion
 * @param {string} notes - Optional approval notes
 * @returns {Promise<object>} Response with success message, updated pro forma, and created outgoing invoice
 */
export const approveProformaConversion = async (invoiceId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/proforma-invoices/${invoiceId}/approve_conversion/`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving pro-forma conversion:', error);
    throw error;
  }
};

/**
 * Reject pro-forma conversion (Financial Approvers only)
 * 
 * @param {number} invoiceId - Pro-forma invoice ID to reject conversion
 * @param {string} reason - Required rejection reason
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const rejectProformaConversion = async (invoiceId, reason) => {
  try {
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is required');
    }
    
    const response = await axiosInstance.post(
      `/api/invoicing/proforma-invoices/${invoiceId}/reject_conversion/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting pro-forma conversion:', error);
    throw error;
  }
};

/**
 * Get pro-forma invoices pending approval (Financial Approvers only)
 * 
 * @returns {Promise<object>} List of pro-forma invoices with status PENDING_APPROVAL
 */
export const getPendingProformaApprovals = async () => {
  try {
    const response = await axiosInstance.get(
      '/api/invoicing/proforma-invoices/pending_approvals/'
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching pending pro-forma approvals:', error);
    throw error;
  }
};

// ============================================================================
// INCOMING INVOICE APPROVAL API CALLS
// ============================================================================

/**
 * Approve an incoming invoice (Financial Approvers only)
 * 
 * @param {number} invoiceId - Incoming invoice ID to approve
 * @param {string} notes - Optional approval notes
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const approveIncomingInvoice = async (invoiceId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/incoming-invoices/${invoiceId}/approve/`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving incoming invoice:', error);
    throw error;
  }
};

/**
 * Reject an incoming invoice (Financial Approvers only)
 * 
 * @param {number} invoiceId - Incoming invoice ID to reject
 * @param {string} reason - Required rejection reason
 * @returns {Promise<object>} Response with success message and updated invoice
 */
export const rejectIncomingInvoice = async (invoiceId, reason) => {
  try {
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is required');
    }
    
    const response = await axiosInstance.post(
      `/api/invoicing/incoming-invoices/${invoiceId}/reject/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting incoming invoice:', error);
    throw error;
  }
};

// ============================================================================
// PURCHASE ORDER APPROVAL API CALLS
// ============================================================================

/**
 * Approve a purchase order (Financial Approvers only)
 * 
 * @param {number} poId - Purchase order ID to approve
 * @param {string} notes - Optional approval notes
 * @returns {Promise<object>} Response with success message and updated PO
 */
export const approvePurchaseOrder = async (poId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/purchase-orders/${poId}/approve/`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving purchase order:', error);
    throw error;
  }
};

/**
 * Reject a purchase order (Financial Approvers only)
 * 
 * @param {number} poId - Purchase order ID to reject
 * @param {string} reason - Required rejection reason
 * @returns {Promise<object>} Response with success message and updated PO
 */
export const rejectPurchaseOrder = async (poId, reason) => {
  try {
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is required');
    }
    
    const response = await axiosInstance.post(
      `/api/invoicing/purchase-orders/${poId}/reject/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting purchase order:', error);
    throw error;
  }
};

// ============================================================================
// PURCHASE ORDER CONVERSION API CALLS
// ============================================================================

/**
 * Submit PO for conversion to incoming invoice (Financial Approval Required)
 * 
 * @param {number} poId - Purchase order ID to submit for conversion
 * @returns {Promise<object>} Response with success message
 */
export const submitPOForConversion = async (poId) => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/purchase-orders/${poId}/submit_for_conversion/`
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting PO for conversion:', error);
    throw error;
  }
};

/**
 * Approve PO conversion and auto-create incoming invoice (Financial Approvers only)
 * 
 * @param {number} poId - Purchase order ID to approve conversion
 * @param {string} notes - Optional approval notes
 * @returns {Promise<object>} Response with success message, updated PO, and created incoming invoice
 */
export const approvePOConversion = async (poId, notes = '') => {
  try {
    const response = await axiosInstance.post(
      `/api/invoicing/purchase-orders/${poId}/approve_conversion/`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving PO conversion:', error);
    throw error;
  }
};

/**
 * Reject PO conversion request (Financial Approvers only)
 * 
 * @param {number} poId - Purchase order ID to reject conversion
 * @param {string} reason - Required rejection reason
 * @returns {Promise<object>} Response with success message and updated PO
 */
export const rejectPOConversion = async (poId, reason) => {
  try {
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is required');
    }
    
    const response = await axiosInstance.post(
      `/api/invoicing/purchase-orders/${poId}/reject_conversion/`,
      { reason }
    );
    return response.data;
  } catch (error) {
    console.error('Error rejecting PO conversion:', error);
    throw error;
  }
};

/**
 * Fetch delivery data pre-formatted for invoice creation
 * @param {number} deliveryId - Delivery ID
 * @param {string} invoiceType - 'OUTGOING' or 'PROFORMA' (default: 'OUTGOING')
 * @returns {Promise<Object>} Mapped delivery data with invoice_data, line_items_data, vessel_info_data
 */
export const fetchDeliveryForInvoice = async (deliveryId, invoiceType = 'OUTGOING', partnerId = null, recapIds = null) => {
  try {
    // Build URL with query params manually to handle array properly
    let url = `/api/invoicing/delivery-prefill/?delivery_id=${deliveryId}&invoice_type=${invoiceType}`;
    
    if (partnerId) {
      url += `&partner_id=${partnerId}`;
    }
    
    // Add recap_ids as multiple query parameters (Django getlist expects this format)
    if (recapIds && recapIds.length > 0) {
      recapIds.forEach(id => {
        url += `&recap_ids=${id}`;
      });
    }
    
    console.log(`[invoicingService] Fetching delivery with URL: ${url}`);
    const response = await axiosInstance.get(url);
    console.log(`Fetched delivery ${deliveryId} for ${invoiceType} invoice pre-fill (partner=${partnerId}, recaps=${recapIds})`);
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery data for invoice:', error);
    throw error;
  }
};

/**
 * Fetch delivery data pre-formatted for purchase order creation
 * @param {number} deliveryId - Delivery ID
 * @param {number} partnerId - Optional CRM partner ID (for multiple sellers)
 * @param {Array<number>} recapIds - Optional list of recap IDs to include
 * @returns {Promise<Object>} Mapped delivery data with po_data, line_items_data, vessel_info_data
 */
/**
 * Bills to receive on one delivery: one row per supplier of bought products and
 * one per third-party cost, each with the supplier invoices already captured.
 * Same access as raising a supplier invoice from the delivery.
 */
export const fetchDeliveryBills = async (deliveryId) => {
  const response = await axiosInstance.get('/api/invoicing/delivery-bills/', {
    params: { delivery_id: deliveryId },
  });
  return response.data;
};

export const fetchDeliveryForPurchaseOrder = async (deliveryId, partnerId = null, recapIds = null) => {
  try {
    // Build URL with query params manually to handle array properly
    let url = `/api/invoicing/delivery-prefill-po/?delivery_id=${deliveryId}`;
    
    if (partnerId) {
      url += `&partner_id=${partnerId}`;
    }
    
    // Add recap_ids as multiple query parameters (Django getlist expects this format)
    if (recapIds && recapIds.length > 0) {
      recapIds.forEach(id => {
        url += `&recap_ids=${id}`;
      });
    }
    
    console.log(`[invoicingService] Fetching delivery for PO with URL: ${url}`);
    const response = await axiosInstance.get(url);
    console.log(`Fetched delivery ${deliveryId} for purchase order pre-fill (partner=${partnerId}, recaps=${recapIds})`);
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery data for purchase order:', error);
    throw error;
  }
};

/**
 * Fetch delivery data pre-formatted for incoming invoice creation
 * @param {number} deliveryId - Delivery ID
 * @param {number} partnerId - Optional CRM partner ID (for multiple sellers)
 * @param {Array<number>} recapIds - Optional list of recap IDs to include
 * @returns {Promise<Object>} Mapped delivery data with invoice_data, line_items_data, vessel_info_data
 */
export const fetchDeliveryForIncomingInvoice = async (deliveryId, partnerId = null, recapIds = null, thirdPartyCostId = null) => {
  try {
    // Build URL with query params manually to handle array properly
    let url = `/api/invoicing/delivery-prefill-incoming/?delivery_id=${deliveryId}`;

    // A supplier invoice for ONE third-party cost (barging, survey...). The
    // server names the supplier and amount from the cost row itself.
    if (thirdPartyCostId) {
      url += `&third_party_cost_id=${thirdPartyCostId}`;
    }
    
    if (partnerId) {
      url += `&partner_id=${partnerId}`;
    }
    
    // Add recap_ids as multiple query parameters (Django getlist expects this format)
    if (recapIds && recapIds.length > 0) {
      recapIds.forEach(id => {
        url += `&recap_ids=${id}`;
      });
    }
    
    console.log(`[invoicingService] Fetching delivery for incoming invoice with URL: ${url}`);
    const response = await axiosInstance.get(url);
    console.log(`Fetched delivery ${deliveryId} for incoming invoice pre-fill (partner=${partnerId}, recaps=${recapIds})`);
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery data for incoming invoice:', error);
    throw error;
  }
};

/**
 * Fetch full delivery details by ID
 * Used to check for multiple partners and recaps before invoice creation
 * @param {number} deliveryId - Delivery ID
 * @returns {Promise<Object>} Delivery object with buyer_contractual_list, seller_contractual_list, sales_recaps, purchase_recaps
 */
export const fetchDeliveryById = async (deliveryId) => {
  try {
    const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/`);
    console.log(`Fetched delivery ${deliveryId} details`);
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery details:', error);
    throw error;
  }
};

/**
 * Create purchase order from delivery
 * @param {number} deliveryId - Delivery ID
 * @param {Object} additionalData - Optional additional/override data
 * @returns {Promise<Object>} Created purchase order
 */
export const createPurchaseOrderFromDelivery = async (deliveryId, additionalData = {}) => {
  try {
    const payload = {
      delivery_id: deliveryId,
      additional_data: additionalData
    };
    const response = await axiosInstance.post('/api/invoicing/purchase-orders/create_from_delivery/', payload);
    console.log(`Created purchase order from delivery ${deliveryId}`);
    return response.data;
  } catch (error) {
    console.error(`Error creating purchase order from delivery ${deliveryId}:`, error);
    throw error;
  }
};

/**
 * Create incoming invoice from delivery
 * @param {number} deliveryId - Delivery ID
 * @param {string} supplierInvoiceNumber - Required supplier invoice number
 * @param {Object} additionalData - Optional additional/override data
 * @returns {Promise<Object>} Created incoming invoice
 */
export const createIncomingInvoiceFromDelivery = async (deliveryId, supplierInvoiceNumber, additionalData = {}) => {
  try {
    const payload = {
      delivery_id: deliveryId,
      supplier_invoice_number: supplierInvoiceNumber,
      additional_data: additionalData
    };
    const response = await axiosInstance.post('/api/invoicing/incoming-invoices/create_from_delivery/', payload);
    console.log(`Created incoming invoice from delivery ${deliveryId}`);
    return response.data;
  } catch (error) {
    console.error(`Error creating incoming invoice from delivery ${deliveryId}:`, error);
    throw error;
  }
};

// ============================================================================
// EXPORT DEFAULT SERVICE OBJECT
// ============================================================================

// ============================================================================
// RELATIONSHIP VISUALIZATION API CALLS
// ============================================================================

/**
 * Get relationship graph data for an outgoing invoice
 */
export const getOutgoingInvoiceRelationships = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/outgoing-invoices/${invoiceId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching outgoing invoice relationships:', error);
    throw error;
  }
};

/**
 * Get relationship graph data for a proforma invoice
 */
export const getProformaInvoiceRelationships = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/proforma-invoices/${invoiceId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching proforma invoice relationships:', error);
    throw error;
  }
};

/**
 * Get relationship graph data for a credit note
 */
export const getCreditNoteRelationships = async (creditNoteId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/credit-notes/${creditNoteId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching credit note relationships:', error);
    throw error;
  }
};

/**
 * Get relationship graph data for a purchase order
 */
export const getPurchaseOrderRelationships = async (poId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/purchase-orders/${poId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching purchase order relationships:', error);
    throw error;
  }
};

/**
 * Get relationship graph data for an incoming invoice
 */
export const getIncomingInvoiceRelationships = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/incoming-invoices/${invoiceId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching incoming invoice relationships:', error);
    throw error;
  }
};

/**
 * Get relationship graph data for a debit note
 */
export const getDebitNoteRelationships = async (debitNoteId) => {
  try {
    const response = await axiosInstance.get(`/api/invoicing/debit-notes/${debitNoteId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching debit note relationships:', error);
    throw error;
  }
};

const invoicingService = {
  attachSupplierDocument,
  // List completeness / failure classification. Exposed on the default export
  // as well as named, because most screens import the default object only.
  searchInvoiceList,
  describeListFailure,
  worstListFailure,

  // Reference data
  fetchPaymentTerms,
  fetchVATRates,
  fetchVATRatesByEntity,
  fetchVATRatesByCountry,
  createVATRate,
  updateVATRate,
  uploadVATRatesCSV,
  fetchGLAccounts,
  fetchGLAccountsByEntity,
  createGLAccount,
  updateGLAccount,
  uploadGLAccountsCSV,
  fetchCostCenters,
  fetchCostCentersByEntity,
  createCostCenter,
  updateCostCenter,
  uploadCostCentresCSV,
  fetchCostCategories,
  createCostCategory,
  updateCostCategory,
  uploadCostCategoriesCSV,
  fetchHMSEntities,
  fetchTemplatesForEntity,
  fetchTemplateTypeOptions,
  fetchInvoicingDropdowns,
  fetchBankingDetails,
  fetchPledgeBanks,
  
  // Dashboard
  fetchInvoicingDashboardStats,
  fetchRecentInvoices,
  
  // Outgoing invoices
  fetchOutgoingInvoices,
  recordOutgoingInvoicePayment,
  clearOutgoingInvoicePayment,
  recordIncomingInvoicePayment,
  fetchOutgoingInvoice,
  createOutgoingInvoice,
  createOutgoingInvoiceFromDelivery,
  updateOutgoingInvoice,
  approveOutgoingInvoice,
  deleteOutgoingInvoice,
  voidOutgoingInvoice,
  downloadOutgoingInvoicePDF,

  // Payment reminders
  fetchPaymentReminders,
  sendPaymentReminders,

  // Pro-forma invoices
  fetchProformaInvoices,
  fetchProformaInvoice,
  createProformaInvoice,
  updateProformaInvoice,
  deleteProformaInvoice,
  submitProformaForConversionApproval,
  approveProformaInvoice,
  rejectProformaInvoice,
  approveProformaConversion,
  rejectProformaConversion,
  getPendingProformaApprovals,
  downloadProformaInvoicePDF,
  
  // Incoming invoices
  fetchIncomingInvoices,
  fetchIncomingInvoice,
  checkInvoiceCrmMatch,
  createIncomingInvoice,
  updateIncomingInvoice,
  deleteIncomingInvoice,
  voidIncomingInvoice,
  uploadSupplierInvoice,
  approveIncomingInvoice,
  rejectIncomingInvoice,
  
  // Purchase orders
  fetchPurchaseOrders,
  fetchPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  markPurchaseOrderReceived,
  approvePurchaseOrder,
  rejectPurchaseOrder,
  submitPOForConversion,
  approvePOConversion,
  rejectPOConversion,
  
  // Credit Notes (AR)
  fetchCreditNotes,
  fetchCreditNote,
  createCreditNote,
  createCreditNoteFromInvoice,
  updateCreditNote,
  deleteCreditNote,
  markCreditNoteApplied,
  generateCreditNotePDF,
  downloadCreditNotePDF,
  submitCreditNoteForApproval,
  approveCreditNote,
  rejectCreditNote,
  
  // Debit Notes (AP)
  fetchDebitNotes,
  fetchDebitNote,
  createDebitNote,
  createDebitNoteFromInvoice,
  updateDebitNote,
  deleteDebitNote,
  markDebitNoteApplied,
  generateDebitNotePDF,
  downloadDebitNotePDF,
  submitDebitNoteForApproval,
  approveDebitNote,
  rejectDebitNote,
  
  // Audit logs
  fetchInvoiceAuditLogs,
  
  // Financial approval
  approveInvoice,
  rejectInvoice,
  unapproveInvoice,
  getPendingApprovals,
  
  // Delivery integration
  fetchDeliveryById,
  fetchDeliveryForInvoice,
  fetchDeliveryForPurchaseOrder,
  fetchDeliveryForIncomingInvoice,
  fetchDeliveryBills,
  createPurchaseOrderFromDelivery,
  createIncomingInvoiceFromDelivery,
  
  // Relationship visualization
  getOutgoingInvoiceRelationships,
  getProformaInvoiceRelationships,
  getCreditNoteRelationships,
  getPurchaseOrderRelationships,
  getIncomingInvoiceRelationships,
  fetchIncomingCaptureFile,
  fetchInvoiceDocuments,
  retryIncomingCaptureFiling,
  getDebitNoteRelationships,
};

export default invoicingService;

/**
 * The APPROVED outgoing invoices raised against a trade recap's deal.
 *
 * Gated on RECAP visibility, not financial access, so a customer trader can
 * call it for their own sale recap even though the rest of the invoicing module
 * refuses them with a 403.
 */
export const getRecapInvoices = async (recapId) => {
  const response = await axiosInstance.get(
    `/api/invoicing/recaps/${recapId}/invoices/`
  );
  return response.data;
};

/**
 * One recap invoice as a PDF blob, for inline viewing.
 * Always a Copy, and never spends the invoice's one Original.
 */
export const getRecapInvoicePDF = async (recapId, invoiceId) => {
  const response = await axiosInstance.get(
    `/api/invoicing/recaps/${recapId}/invoices/${invoiceId}/pdf/`,
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * The BDNs recorded against this deal's deliveries, listed on the sales recap.
 *
 * Gated on RECAP visibility, never on delivery access: a customer trader has
 * no delivery access at all, so the delivery's own BDN endpoint refuses them
 * by design and must not be called from the recap screen.
 */
export const getRecapBDNs = async (recapId) => {
  const response = await axiosInstance.get(
    `/api/invoicing/recaps/${recapId}/bdns/`
  );
  return response.data;
};

/**
 * One BDN file from the sales recap, as a blob for inline viewing.
 *
 * Same recap door as the list. The server proves the document belongs to a
 * delivery of THIS recap from HMS's own records before it asks the archive,
 * so an id from another deal is "not found" and the archive is never called.
 */
export const getRecapBDNDocument = async (recapId, documentId) => {
  const response = await axiosInstance.get(
    `/api/invoicing/recaps/${recapId}/bdns/${documentId}/`,
    { responseType: 'blob' }
  );
  return response.data;
};
